import { memo, useMemo } from 'react';
import { Configure, InstantSearch } from 'react-instantsearch-hooks-web';
import { Grid, Stack, Typography } from '@mui/material';
import { algoliaClient } from '@client/algolia';
import { Hits } from '@client/algolia/InstantSearch/Hits';
import { getAlgoliaIndexName, serializeFilters } from '@sda/utils/algolia';
import { BeaconStatuses } from '@sda/enums';
import { getDateRange } from '@sda/utils/date';
import { AlgoliaIndices } from '@sda/enums/algolia';
import { getLocation } from '@sda/utils/location';
import { useIsMobile } from '@client/hooks/dom.hooks';
import { getEventLocation, getRoundedFollowers } from '@sda/utils/string';
import { SiteContainer } from '@client/components/common/SiteContainer';
import { ConnectCard } from '../common/ConnectCard';
import type { FetchDataType } from '@sda/sdk/interfaces';
import type { Beacon } from '@sda/interfaces/beacon';
import type { AlgoliaFilter } from '@sda/utils/algolia/interfaces';
import type { AlgoliaBeacon } from '@sda/helpers/algolia.helpers';

const defaultFilters = {
  'publicBeacon.endDate._seconds': {
    type: 'futureDate',
  },
  'publicBeacon.beaconStatus': {
    type: 'string',
    value: BeaconStatuses.ACTIVE as string,
  },
  'publicBeacon.isPublicCall': {
    type: 'boolean',
    value: true,
  },
  hasPublicImageUrl: {
    type: 'tag',
  },
};

const attributesToRetrieve = [
  'publicBeacon.profileData.name',
  'publicBeacon.profileData.publicImageUrl',
  'publicBeacon.profileData.score',
  'publicBeacon.profileData.numReviews',
  'publicBeacon.profileData.badge',
  'publicBeacon.profileData.spotifyMonthlyActiveListeners',
  'publicBeacon.city',
  'publicBeacon.region',
  'publicBeacon.startDate',
  'publicBeacon.endDate',
  'publicBeacon.isFeatured',
];

export const NearbyShowCalls = memo<Pick<Beacon & FetchDataType, 'id' | 'latLng' | 'profileType'>>(({ id, latLng, profileType }) => {
  const isMobile = useIsMobile();

  const filters = useMemo(() => {
    const refinedFilters = {
      ...defaultFilters,
      objectID: {
        type: 'string',
        value: id,
        not: true,
      },
      'publicBeacon.profileType': {
        type: 'string',
        value: profileType as string,
      },
    };

    return serializeFilters(refinedFilters as Record<string, AlgoliaFilter>);
  }, [id, profileType]);

  const filterLocation = useMemo(() => {
    const location = getLocation(latLng);

    return `${location.lat}, ${location.lng}`;
  }, [latLng]);

  return (
    <SiteContainer maxWidth="narrow">
      <InstantSearch
        searchClient={algoliaClient}
        indexName={getAlgoliaIndexName(AlgoliaIndices.BEACONS)}
      >
        <Configure
          hitsPerPage={3}
          filters={filters}
          aroundLatLng={filterLocation}
          aroundRadius="all"
          attributesToRetrieve={attributesToRetrieve}
          attributesToHighlight={[]}
        />

        <Stack spacing={isMobile ? 3 : 4}>
          <Typography variant="subtitle2">
            Nearby Show Calls
          </Typography>

          <div>
            <Hits<any, AlgoliaBeacon>
              component={Grid}
              container
              spacing={3}
              hitComponent={({ hit }) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                >
                  <ConnectCard
                    imgSrc={hit.publicBeacon.profileData.publicImageUrl}
                    path={`/connect/${hit.objectID}`}
                    title={hit.publicBeacon.profileData.name}
                    location={getEventLocation(hit.publicBeacon)}
                    numReviews={hit.publicBeacon.profileData.numReviews}
                    score={hit.publicBeacon.profileData.score}
                    dateRange={getDateRange(hit.publicBeacon)}
                    isFeatured={hit.publicBeacon.isFeatured}
                    badge={hit.publicBeacon.profileData.badge}
                    spotifyMonthlyActiveListeners={getRoundedFollowers(hit.publicBeacon.profileData.spotifyMonthlyActiveListeners, 'compact')}
                  />
                </Grid>
              )}
            />
          </div>
        </Stack>
      </InstantSearch>
    </SiteContainer>
  );
});
